import { Component, EventEmitter, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AuthService } from '../../../features/auth/auth.service';
import { PasswordStrength, getPasswordStrength } from '../../../core/utils/register-validation.util';

interface ChangePasswordForm {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

const COGNITO_ERROR_MESSAGES: Record<string, string> = {
  NotAuthorizedException: 'La contraseña actual es incorrecta',
  LimitExceededException: 'Demasiados intentos. Intenta más tarde.',
};

@Component({
  selector: 'app-change-password-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './change-password-dialog.component.html',
  styleUrl: './change-password-dialog.component.css',
})
export class ChangePasswordDialogComponent {
  @Output() closed = new EventEmitter<void>();

  form: ChangePasswordForm = {
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  };
  passwordStrength: PasswordStrength = '';
  isLoading = false;
  errorMessage = '';
  succeeded = false;

  constructor(private authService: AuthService) {}

  onNewPasswordInput(): void {
    this.passwordStrength = getPasswordStrength(this.form.newPassword);
  }

  async submit(): Promise<void> {
    if (this.isLoading) return;

    this.errorMessage = this.validate();
    if (this.errorMessage) return;

    this.isLoading = true;
    try {
      await this.authService.changePassword(this.form.currentPassword, this.form.newPassword);
      this.succeeded = true;
    } catch (err: any) {
      const code = err?.code || err?.name;
      this.errorMessage = COGNITO_ERROR_MESSAGES[code] || err?.message || 'No fue posible cambiar la contraseña';
    } finally {
      this.isLoading = false;
    }
  }

  requestClose(): void {
    if (this.isLoading) return;
    this.closed.emit();
  }

  private validate(): string {
    const { currentPassword, newPassword, confirmPassword } = this.form;

    if (!currentPassword || !newPassword || !confirmPassword) {
      return 'Completa todos los campos';
    }
    if (newPassword !== confirmPassword) {
      return 'Las contraseñas no coinciden';
    }
    if (getPasswordStrength(newPassword) === 'weak') {
      return 'La contraseña es muy débil';
    }
    if (newPassword === currentPassword) {
      return 'La nueva contraseña debe ser distinta a la actual';
    }
    return '';
  }
}
